// A site's stage: which of steps 00 to 08 are Not Started, In Progress or Done.
// Words: COHESION_CHANGE_LOG.md changes 1 and 2 (mockup MOCKUP_COHESION_FIVE.html, approved 2026-10-24, 6:14 AM).
//
// Rules:
// * 00 to 04 are field work the app cannot see the end of. Done is the admin's call: a check on the hub,
//   saved on the site record. The app only moves a step from Not Started to In Progress, when work starts.
// * 05, 06 and 08 finish in the records. The app works out Done on its own.
// * 07 Verification is not built. It is always Not Started and is left out of the count.
// * The hub and the server's Refresh both work the stage out here, from the same facts, so they agree.
// Pure: facts in, lights out.

export type Light = "gray" | "yellow" | "green";

/** The steps with a Done check on the hub. */
export const MANUAL_STEPS = ["00", "01", "02", "03", "04"] as const;
export type ManualStep = (typeof MANUAL_STEPS)[number];

export function isManualStep(v: unknown): v is ManualStep {
  return typeof v === "string" && (MANUAL_STEPS as readonly string[]).includes(v);
}

/** Saved on the site at stepsDone.{step} by server call 100 markStepDone. */
export interface StepMark {
  at: string;
  badgeId: string;
  holderName: string;
}

export type StepMarks = Partial<Record<ManualStep, StepMark>>;

/** The marks a site record holds, read safely. Anything not shaped right is left out. */
export function marksOf(raw: unknown): StepMarks {
  const out: StepMarks = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isManualStep(k) || !v || typeof v !== "object") continue;
    const m = v as Record<string, unknown>;
    out[k] = { at: String(m.at ?? ""), badgeId: String(m.badgeId ?? ""), holderName: String(m.holderName ?? "") };
  }
  return out;
}

/** What the stage is worked out from. Everything here is cheap for the server to read. */
export interface StageFacts {
  /** Work has started on 00 to 04. */
  started: Record<ManualStep, boolean>;
  marks: StepMarks;
  /** The current study's state ("Imported", "Published", "Labels Created"), or null with no study. */
  studyState: string | null;
  plantManager: boolean;
  city: boolean;
  driveFolder: boolean;
}

/** The light for each of steps 00 to 08, in order. */
export function stageLights(f: StageFacts): Light[] {
  const manual = MANUAL_STEPS.map((s): Light => (f.marks[s] ? "green" : f.started[s] ? "yellow" : "gray"));
  const study: Light = !f.studyState ? "gray" : f.studyState === "Imported" ? "yellow" : "green";
  const labels: Light = f.studyState === "Labels Created" ? "green" : f.studyState === "Published" ? "yellow" : "gray";
  // 08 turns on the Plant Manager. City and the Drive folder come with Add Site (change 2).
  const info: Light = !f.plantManager ? "gray" : f.city && f.driveFolder ? "green" : "yellow";
  return [...manual, study, labels, "gray", info];
}

/** Nine letters, steps 00 to 08: g done, y in progress, n not started. What the site record keeps. */
export function stripOfLights(lights: readonly Light[]): string {
  return lights.map((l) => (l === "green" ? "g" : l === "yellow" ? "y" : "n")).join("");
}

export function stageStrip(f: StageFacts): string {
  return stripOfLights(stageLights(f));
}

/** The study a site works from: the newest one not replaced. Studies come newest first. */
export function currentStudyState(studies: readonly { state: string; importedAt?: string }[]): string | null {
  const sorted = [...studies].sort((a, b) => String(b.importedAt ?? "").localeCompare(String(a.importedAt ?? "")));
  return sorted.find((s) => s.state !== "Replaced")?.state ?? null;
}

/** A QEW bag tag counts as started once someone claimed it. The 99 Unclaimed tags Add Site makes do not. */
export function tagClaimed(status: unknown): boolean {
  return typeof status === "string" && status !== "" && status !== "Unclaimed";
}

/** "Marked Done · Robert Stuart · Today 6:12 AM". */
export function markLine(m: StepMark, when: string): string {
  return ["Marked Complete", m.holderName || m.badgeId, when].filter(Boolean).join(" · ");
}

/** "Updated Today 5:34 AM" on Home: the oldest saved stage, so it never claims more than is true. */
export function oldestSavedAt(stages: readonly ({ savedAt?: string } | null | undefined)[]): string | null {
  const at = stages.map((s) => s?.savedAt ?? "").filter(Boolean).sort();
  return at[0] ?? null;
}

// ---- Add Site number (change 4) ----

/**
 * The number a new site takes when it is not from a plant: one past the highest site or plant number.
 * Once 99 is used, the lowest number still free. Null when all 100 are used.
 */
export function nextSiteNumber(taken: Iterable<string>): string | null {
  const used = new Set([...taken].filter((n) => /^\d{2}$/.test(n)).map(Number));
  const high = used.size ? Math.max(...used) : -1;
  if (high < 99) return String(high + 1).padStart(2, "0");
  for (let i = 0; i < 100; i++) if (!used.has(i)) return String(i).padStart(2, "0");
  return null;
}

// ---- Back to the site (change 5) ----

const SITE_NUM = /^\d{2}$/;

/**
 * Where a page's top left back link goes, from its address: a site number (its hub), "home",
 * or null when the page keeps its own back. Pages opened from a hub row, their All Sites lists,
 * and the top-level pages that had no back (ONE_BACK_WORDS.md).
 */
export function siteBackOf(parts: readonly string[]): string | "home" | null {
  const [top = "", a = "", b, c] = parts;
  const site = SITE_NUM.test(a) ? a : "";
  switch (top) {
    case "gloves":
      if (a === "p") return null;
      return site ? site : "home";
    case "work-requests":
    case "near-miss":
    case "contractors":
      if (a === "r") return null;
      return site ? site : "home";
    case "training":
      return site && !b ? site : null;
    case "assets":
      if (!a) return "home";
      return site && (!b || b === "labels") ? site : null;
    case "survey":
      if (!a) return "home";
      return site && !b ? site : null;
    case "af-labels":
      // The study review goes back to the study list (ONE_BACK_WORDS.md), which goes back to the site.
      return site && !b ? site : null;
    case "people":
      if (!a) return "home";
      return a === "tags" && SITE_NUM.test(b ?? "") && !c ? b! : null;
    // Top-level pages opened from Home that had no back (ONE_BACK_WORDS.md).
    case "settings":
    case "lists":
      return a ? null : "home";
    case "email-log":
      return a === "m" ? null : "home";
    default:
      return null;
  }
}
